'use strict';

/*
 * Modelo geométrico compartido de la Forma 28 de LiSEC.
 *
 * R* no es el radio de la circunferencia: es el Stichmaß (flecha) medido
 * perpendicularmente a la cuerda que une (0, H) con (L, H1).
 * Este módulo CommonJS deliberadamente no depende ni de Angular ni de otros
 * servicios del backend para que Canvas, PDF, dimensiones y V40 usen
 * exactamente el mismo cálculo.
 */

const EPSILON = 0.000001;
const TWO_PI = 2 * Math.PI;

function toNumber(valueIn) {
  const value = Number(valueIn);
  return Number.isFinite(value) ? value : 0;
}

function normalizeAngle(angleIn) {
  let angle = angleIn % TWO_PI;
  if (angle < 0) angle += TWO_PI;
  return angle;
}

function pointOnClockwiseArc(point, arc, includeEnds) {
  const angle = Math.atan2(point.y - arc.centerY, point.x - arc.centerX);
  const distance = normalizeAngle(arc.startAngle - angle);
  if (includeEnds) {
    return distance >= -EPSILON && distance <= (arc.clockwiseSweep + EPSILON);
  }
  return distance > EPSILON && distance < (arc.clockwiseSweep - EPSILON);
}

function hasInteriorArcPoint(points, arc, predicate) {
  return points.some(point => predicate(point) && pointOnClockwiseArc(point, arc, false));
}

function circleHorizontalIntersections(arc, y) {
  const dy = y - arc.centerY;
  const square = (arc.circleRadius * arc.circleRadius) - (dy * dy);
  if (square < -EPSILON) return [];
  const dx = Math.sqrt(Math.max(0, square));
  return [
    { x: arc.centerX - dx, y },
    { x: arc.centerX + dx, y }
  ];
}

function circleVerticalIntersections(arc, x) {
  const dx = x - arc.centerX;
  const square = (arc.circleRadius * arc.circleRadius) - (dx * dx);
  if (square < -EPSILON) return [];
  const dy = Math.sqrt(Math.max(0, square));
  return [
    { x, y: arc.centerY - dy },
    { x, y: arc.centerY + dy }
  ];
}

function arcIntersectsStraightContour(arc, l, h, h1) {
  // El arco sólo puede invalidar este contorno simple si vuelve a cortar uno
  // de sus tres lados rectos en un punto distinto de sus extremos.
  const onBottom = point => point.x >= -EPSILON && point.x <= (l + EPSILON);
  if (hasInteriorArcPoint(circleHorizontalIntersections(arc, 0), arc, onBottom)) {
    return true;
  }

  const onLeft = point => point.y >= -EPSILON && point.y <= (h + EPSILON);
  if (hasInteriorArcPoint(circleVerticalIntersections(arc, 0), arc, onLeft)) {
    return true;
  }

  const onRight = point => point.y >= -EPSILON && point.y <= (h1 + EPSILON);
  return hasInteriorArcPoint(circleVerticalIntersections(arc, l), arc, onRight);
}

function getArcBounds(arc) {
  const points = [
    { x: arc.xLeft, y: arc.yLeft },
    { x: arc.xRight, y: arc.yRight }
  ];
  [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2].forEach(angle => {
    const point = {
      x: arc.centerX + (arc.circleRadius * Math.cos(angle)),
      y: arc.centerY + (arc.circleRadius * Math.sin(angle))
    };
    if (pointOnClockwiseArc(point, arc, true)) points.push(point);
  });
  return {
    minX: Math.min.apply(null, points.map(point => point.x)),
    maxX: Math.max.apply(null, points.map(point => point.x)),
    minY: Math.min.apply(null, points.map(point => point.y)),
    maxY: Math.max.apply(null, points.map(point => point.y))
  };
}

function getForm28SagittaInfo(lIn, hIn, h1In) {
  const l = toNumber(lIn);
  const h = toNumber(hIn);
  const h1 = toNumber(h1In);
  if (!(l > 0) || !(h > 0) || h1 < 0) return null;

  const chord = Math.hypot(l, h1 - h);
  if (!(chord > 0)) return null;
  const heightDifference = Math.abs(h - h1);
  // LiSEC no permite que el arco invada las bandas laterales del marco. El
  // límite se alcanza cuando el centro de la circunferencia queda a la misma
  // cota que el extremo más bajo y el arco es tangente a ese lateral.
  // Esta forma racionalizada evita pérdida de precisión cuando H≈H1.
  const noOverhangSagitta =
    (chord * l) / (2 * (chord + heightDifference));

  return {
    min: 1,
    max: Math.floor(noOverhangSagitta + EPSILON),
    maxExact: noOverhangSagitta,
    chord,
    noOverhangSagitta,
    semicircleSagitta: chord / 2
  };
}

function buildArcCore(l, h, h1, sagitta) {
  const chord = Math.hypot(l, h1 - h);
  const dx = l;
  const dy = h1 - h;
  const nx = -dy / chord;
  const ny = dx / chord;
  const midX = l / 2;
  const midY = (h + h1) / 2;
  const circleRadius = ((chord * chord) / (8 * sagitta)) + (sagitta / 2);
  const centerX = midX + (nx * (sagitta - circleRadius));
  const centerY = midY + (ny * (sagitta - circleRadius));
  const startAngle = Math.atan2(h - centerY, -centerX);
  // 4*atan(2s/c) representa inequívocamente el arco menor y su límite
  // semicircular. La Forma 28 de catálogo no admite cruzar ese límite.
  const clockwiseSweep = 4 * Math.atan2(2 * sagitta, chord);
  const arc = {
    sagitta,
    radius: sagitta,
    chord,
    circleRadius,
    centerX,
    centerY,
    startAngle,
    clockwiseSweep,
    xLeft: 0,
    xRight: l,
    yLeft: h,
    yRight: h1
  };
  arc.bounds = getArcBounds(arc);
  return arc;
}

function translatePoint(point, offsetX, offsetY) {
  return { x: point.x + offsetX, y: point.y + offsetY };
}

function buildForm28Geometry(paramsIn, optionsIn) {
  const params = paramsIn || {};
  const options = optionsIn || {};
  const b1 = Math.max(0, toNumber(params.b1));
  const b2 = Math.max(0, toNumber(params.b2));
  const b3 = Math.max(0, toNumber(params.b3));
  const l = toNumber(params.l);
  const h = toNumber(params.h);
  const h1 = toNumber(params.h1);
  const info = getForm28SagittaInfo(l, h, h1);
  if (!info) return null;
  if (info.max < info.min) return null;

  let sagitta = toNumber(params.r);
  const sagittaIsInRange =
    sagitta >= info.min &&
    sagitta <= (info.maxExact + EPSILON);
  if (!sagittaIsInRange) {
    if (!options.clampRadius && !options.clampSagitta) return null;
    sagitta = Math.max(
      info.min,
      Math.min(sagitta > 0 ? sagitta : info.min, info.max)
    );
  }

  const arc = buildArcCore(l, h, h1, sagitta);
  const contourIsSimple = !arcIntersectsStraightContour(arc, l, h, h1);
  if (!contourIsSimple && !options.allowInvalidContour) return null;

  const requestedSegments = Math.round(toNumber(options.segments));
  let segmentCount = Math.max(
    16,
    requestedSegments > 0
      ? requestedSegments
      : Math.ceil(64 * arc.clockwiseSweep / TWO_PI)
  );
  // El punto t=0,5 es precisamente donde se materializa R*. Mantener un
  // número par hace que Canvas/PDF incluyan siempre esa flecha exacta.
  if ((segmentCount % 2) !== 0) segmentCount += 1;
  const rawArcPoints = [];
  for (let i = 0; i <= segmentCount; i++) {
    const angle = arc.startAngle - (arc.clockwiseSweep * i / segmentCount);
    rawArcPoints.push({
      x: arc.centerX + (arc.circleRadius * Math.cos(angle)),
      y: arc.centerY + (arc.circleRadius * Math.sin(angle))
    });
  }

  const primitiveMinX = Math.min(0, l, arc.bounds.minX);
  const primitiveMaxX = Math.max(0, l, arc.bounds.maxX);
  const primitiveMinY = Math.min(0, h, h1, arc.bounds.minY);
  const primitiveMaxY = Math.max(0, h, h1, arc.bounds.maxY);
  const outerMinX = primitiveMinX - b1;
  const outerMaxX = primitiveMaxX + b2;
  const outerMinY = primitiveMinY;
  const outerMaxY = primitiveMaxY + b3;
  const offsetX = -outerMinX;
  const offsetY = -outerMinY;

  const translatedBounds = {
    minX: arc.bounds.minX + offsetX,
    maxX: arc.bounds.maxX + offsetX,
    minY: arc.bounds.minY + offsetY,
    maxY: arc.bounds.maxY + offsetY
  };
  const semicircleDelta = sagitta - info.semicircleSagitta;
  const arcType = Math.abs(semicircleDelta) <= EPSILON
    ? 'semicircle'
    : (semicircleDelta < 0 ? 'minor' : 'major');

  return {
    sagitta,
    // Alias conservado para consumidores y datos históricos que llaman R al campo.
    radius: sagitta,
    radiusIsValid: sagittaIsInRange && contourIsSimple,
    sagittaIsValid: sagittaIsInRange && contourIsSimple,
    contourIsSimple,
    validationError: contourIsSimple ? null : 'self-intersection',
    radiusRange: info,
    sagittaInfo: info,
    arcType,
    chord: arc.chord,
    circleRadius: arc.circleRadius,
    centerX: arc.centerX + offsetX,
    centerY: arc.centerY + offsetY,
    clockwiseSweep: arc.clockwiseSweep,
    xLeft: offsetX,
    xRight: l + offsetX,
    yBottom: offsetY,
    yLeft: h + offsetY,
    yRight: h1 + offsetY,
    primitiveLeft: primitiveMinX + offsetX,
    primitiveRight: primitiveMaxX + offsetX,
    primitiveBottom: primitiveMinY + offsetY,
    primitiveTop: primitiveMaxY + offsetY,
    primitiveWidth: primitiveMaxX - primitiveMinX,
    primitiveHeight: primitiveMaxY - primitiveMinY,
    width: outerMaxX - outerMinX,
    height: outerMaxY - outerMinY,
    offsetX,
    offsetY,
    bounds: translatedBounds,
    arcPoints: rawArcPoints.map(point => translatePoint(point, offsetX, offsetY))
  };
}

// Nombre histórico conservado para no romper integraciones externas.
const getForm28RadiusRange = getForm28SagittaInfo;

module.exports = {
  buildForm28Geometry,
  getForm28RadiusRange,
  getForm28SagittaInfo
};
